// src/utils/lessonCloseoutWriter.js
//
// Pass 84 — the orchestration writer for a booking-linked post-lesson closeout. It composes TWO existing
// idempotent primitives behind the explicit-occurrence gate; it introduces NO new collection, NO new lesson
// identity and NO direct Firestore write of its own:
//
//   1. `getOrCreateLessonInstanceForBooking` (Pass 66) — deterministic id `lesson_bk_<hash(org::bookingId)>`,
//      transactional get-or-create → retry / double-tap / second device → 0 duplicate lessons.
//   2. `saveQuickLessonLog` (Pass 68) — merges ONLY the delivery fields onto that lesson (lineage/identity
//      preserved), transactional get-or-write on the same deterministic id → 0 duplicate evidence.
//
// Because BOTH steps key on the same deterministic booking-lesson id, the whole closeout is idempotent: a retry
// after partial success (lesson created but log not yet saved, or a repeated save) converges to ONE lesson with
// ONE set of delivery evidence. The explicit-occurrence gate (`assertCloseoutReady`) runs FIRST, so elapsed time
// / booking.status can never produce a delivery-bearing lesson through this path.

import { getOrCreateLessonInstanceForBooking } from './lessonHandoff';
import { saveQuickLessonLog } from './quickLessonLogWriter';
import { assertCloseoutReady, buildCloseoutDelivery } from './lessonCloseout';

// saveLessonCloseout(db, { booking, occurred, note, outcomeChoice, explicitOutcome, recommendedNextSkillId,
//                          status, userData, actorAuthUserId, actorInstructorId, organizationId, readOnly })
//   • `occurred` MUST be explicit boolean true (the instructor's "this lesson took place" acknowledgement).
//   • A COMPLETED closeout REQUIRES a non-empty human note (Pass 84.1). Outcome/target skill are optional enrichment.
//   • `explicitOutcome` — only when true is `outcomeChoice` applied; otherwise the existing lesson outcome is preserved.
//   • `status` defaults to 'completed'; pass 'draft' to save without completing.
//   • CLOSEOUT MODE is ADDITIVE — it preserves ALL pre-existing structured stage progress (never rebuilds stages).
export async function saveLessonCloseout(db, {
  booking = {}, occurred = false, note = '', outcomeChoice = null, explicitOutcome = false,
  recommendedNextSkillId = '', status = 'completed', ...ctx
} = {}) {
  // 1) Explicit-occurrence + note gate. A completed closeout requires the acknowledgement AND a non-empty human note;
  //    a plain 'draft' save may skip the note but still needs the explicit acknowledgement.
  if (status === 'completed') {
    assertCloseoutReady({ occurred, note });
  } else if (occurred !== true) {
    const e = new Error('Confirm the lesson took place before saving the closeout.');
    e.code = 'CLOSEOUT_NOT_ACKNOWLEDGED';
    throw e;
  }

  // 2) Idempotent get-or-create of the canonical booking lesson (authorization + eligibility enforced here).
  const { lessonId } = await getOrCreateLessonInstanceForBooking(db, booking, ctx);

  // 3) Idempotent, ADDITIVE closeout merge (mode:'closeout') — writes only the human note (+ an explicitly chosen
  //    outcome); pre-existing stages / drills / notes / outcome are preserved (lineage/identity preserved too).
  const delivery = buildCloseoutDelivery({ note, outcomeChoice, explicitOutcome, recommendedNextSkillId });
  const res = await saveQuickLessonLog(db, { lessonInstanceId: lessonId, delivery, status, mode: 'closeout', ...ctx });
  return { lessonInstanceId: lessonId, created: !!res.created, status: res.status };
}
